import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabaseClient";
import packageJson from "../../package.json";

import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import {
    Form,
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { LoadingButtonContent } from "@/components/ui/loading-button-content";
import { useToast } from "@/hooks/use-toast";

import { resetMockData } from "@/lib/mockData";
import { Slider } from "@/components/ui/slider";

const discountFormSchema = z.object({
    product_price: z.number().min(0).max(1000000),
    discount_percentage: z.number().min(0).max(100),
    sales_volume: z.number().min(0).max(1000000),
    conversion_rate_increase: z.number().min(0).max(100),
    cost_of_goods_sold: z.number().min(0).max(1000000),
});

function Discounts() {
    const { session, isDemoMode } = useAuth();
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState<{
        type: "success" | "error";
        text: string;
    } | null>(null);
    const [results, setResults] = useState<{
        baseline: { revenue: number; profit: number; units: number };
        discount: { revenue: number; profit: number; units: number };
    } | null>(null);

    const { toast } = useToast();

    const discountForm = useForm<z.infer<typeof discountFormSchema>>({
        resolver: zodResolver(discountFormSchema),
        defaultValues: {
            product_price: 100,
            discount_percentage: 20,
            sales_volume: 500,
            conversion_rate_increase: 30,
            cost_of_goods_sold: 50,
        },
    });

    const handleDiscountSubmit = async (
        values: z.infer<typeof discountFormSchema>
    ) => {
        setLoading(true);
        const baseline_revenue = values.product_price * values.sales_volume;
        const discount_sales_volume =
            values.sales_volume * (1 + values.conversion_rate_increase / 100);
        const discount_product_price =
            values.product_price * (1 - values.discount_percentage / 100);
        const discount_revenue = discount_product_price * discount_sales_volume;
        const baseline_profit =
            baseline_revenue - values.cost_of_goods_sold * values.sales_volume;
        const discount_profit =
            discount_revenue -
            values.cost_of_goods_sold * discount_sales_volume;
        console.log(baseline_profit, discount_profit);

        // Calculate and store results for display
        setResults({
            baseline: {
                revenue: baseline_revenue,
                profit: baseline_profit,
                units: values.sales_volume,
            },
            discount: {
                revenue: discount_revenue,
                profit: discount_profit,
                units: discount_sales_volume,
            },
        });
        setLoading(false);
    };

    const handleResetDemoData = async () => {
        setIsDemoDataResetting(true);
        await resetMockData(session?.user?.id);
        setIsDemoDataResetting(false);
    };

    return (
        <div className="space-y-6">
            {message && (
                <Alert
                    variant={
                        message.type === "error" ? "destructive" : "default"
                    }
                >
                    <AlertTitle>
                        {message.type === "error" ? "Error" : "Success"}
                    </AlertTitle>
                    <AlertDescription>{message.text}</AlertDescription>
                </Alert>
            )}

            <Card>
                <CardHeader>
                    <CardTitle>Discount Settings</CardTitle>
                    <CardDescription>
                        Enter details of your planned discount strategy.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <Form {...discountForm}>
                        <form
                            onSubmit={discountForm.handleSubmit(
                                handleDiscountSubmit
                            )}
                            className="space-y-4"
                        >
                            <FormField
                                control={discountForm.control}
                                name="product_price"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Product Price</FormLabel>
                                        <FormControl>
                                            <div className="flex items-center gap-4">
                                                <Slider
                                                    value={[field.value]}
                                                    min={0}
                                                    max={1000}
                                                    step={1}
                                                    onValueChange={(value) =>
                                                        field.onChange(value[0])
                                                    }
                                                    className="flex-1"
                                                />
                                                <Input
                                                    type="number"
                                                    value={field.value}
                                                    onChange={(e) => {
                                                        const value =
                                                            parseFloat(
                                                                e.target.value
                                                            );
                                                        field.onChange(
                                                            isNaN(value)
                                                                ? 0
                                                                : value
                                                        );
                                                    }}
                                                    className="w-20"
                                                />
                                            </div>
                                        </FormControl>
                                        <FormDescription>
                                            What is the full price of the
                                            product?
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={discountForm.control}
                                name="cost_of_goods_sold"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>
                                            Cost of Goods Sold
                                        </FormLabel>
                                        <FormControl>
                                            <div className="flex items-center gap-4">
                                                <Slider
                                                    value={[field.value]}
                                                    min={0}
                                                    max={1000}
                                                    step={1}
                                                    onValueChange={(value) =>
                                                        field.onChange(value[0])
                                                    }
                                                    className="flex-1"
                                                />
                                                <Input
                                                    type="number"
                                                    value={field.value}
                                                    onChange={(e) => {
                                                        const value =
                                                            parseFloat(
                                                                e.target.value
                                                            );
                                                        field.onChange(
                                                            isNaN(value)
                                                                ? 0
                                                                : value
                                                        );
                                                    }}
                                                    className="w-20"
                                                />
                                            </div>
                                        </FormControl>
                                        <FormDescription>
                                            What is your cost per unit?
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={discountForm.control}
                                name="sales_volume"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>Sales Volume</FormLabel>
                                        <FormControl>
                                            <div className="flex items-center gap-4">
                                                <Slider
                                                    value={[field.value]}
                                                    min={0}
                                                    max={1000}
                                                    step={1}
                                                    onValueChange={(value) =>
                                                        field.onChange(value[0])
                                                    }
                                                    className="flex-1"
                                                />
                                                <Input
                                                    type="number"
                                                    value={field.value}
                                                    onChange={(e) => {
                                                        const value =
                                                            parseFloat(
                                                                e.target.value
                                                            );
                                                        field.onChange(
                                                            isNaN(value)
                                                                ? 0
                                                                : value
                                                        );
                                                    }}
                                                    className="w-20"
                                                />
                                            </div>
                                        </FormControl>
                                        <FormDescription>
                                            How many units do you expect to
                                            sell?
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={discountForm.control}
                                name="discount_percentage"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>
                                            Discount Percentage
                                        </FormLabel>
                                        <FormControl>
                                            <div className="flex items-center gap-4">
                                                <Slider
                                                    value={[field.value]}
                                                    min={0}
                                                    max={100}
                                                    step={1}
                                                    onValueChange={(value) =>
                                                        field.onChange(value[0])
                                                    }
                                                    className="flex-1"
                                                />
                                                <Input
                                                    type="number"
                                                    value={field.value}
                                                    onChange={(e) => {
                                                        const value =
                                                            parseFloat(
                                                                e.target.value
                                                            );
                                                        field.onChange(
                                                            isNaN(value)
                                                                ? 0
                                                                : value
                                                        );
                                                    }}
                                                    className="w-20"
                                                />
                                            </div>
                                        </FormControl>
                                        <FormDescription>
                                            What percentage discount are you
                                            planning to offer?
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <FormField
                                control={discountForm.control}
                                name="conversion_rate_increase"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>
                                            Conversion Rate Increase
                                        </FormLabel>
                                        <FormControl>
                                            <div className="flex items-center gap-4">
                                                <Slider
                                                    value={[field.value]}
                                                    min={0}
                                                    max={100}
                                                    step={1}
                                                    onValueChange={(value) =>
                                                        field.onChange(value[0])
                                                    }
                                                    className="flex-1"
                                                />
                                                <Input
                                                    type="number"
                                                    value={field.value}
                                                    onChange={(e) => {
                                                        const value =
                                                            parseFloat(
                                                                e.target.value
                                                            );
                                                        field.onChange(
                                                            isNaN(value)
                                                                ? 0
                                                                : value
                                                        );
                                                    }}
                                                    className="w-20"
                                                />
                                            </div>
                                        </FormControl>
                                        <FormDescription>
                                            What percentage increase in
                                            conversion rate do you expect from
                                            this discount?
                                        </FormDescription>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />

                            <Button
                                type="submit"
                                className="w-[120px]"
                                disabled={loading}
                            >
                                <LoadingButtonContent loading={loading}>
                                    Calculate
                                </LoadingButtonContent>
                            </Button>
                        </form>
                    </Form>
                </CardContent>
            </Card>

            {results && (
                <Card>
                    <CardHeader>
                        <CardTitle>Discount Analysis Results</CardTitle>
                        <CardDescription>
                            Comparison of revenue and profit with and without
                            the discount.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="overflow-x-auto">
                            <table className="w-full border-collapse">
                                <thead>
                                    <tr className="border-b">
                                        <th className="text-left py-2 px-4"></th>
                                        <th className="text-right py-2 px-4">
                                            No Discount
                                        </th>
                                        <th className="text-right py-2 px-4">
                                            With Discount (
                                            {
                                                discountForm.getValues()
                                                    .discount_percentage
                                            }
                                            %)
                                        </th>
                                        <th className="text-right py-2 px-4">
                                            Difference
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr className="border-b">
                                        <td className="py-2 px-4 font-medium">
                                            Units Sold
                                        </td>
                                        <td className="text-right py-2 px-4">
                                            {results.baseline.units.toLocaleString()}
                                        </td>
                                        <td className="text-right py-2 px-4">
                                            {results.discount.units.toLocaleString()}
                                        </td>
                                        <td
                                            className={`text-right py-2 px-4 ${
                                                results.discount.units >
                                                results.baseline.units
                                                    ? "text-green-600"
                                                    : "text-red-600"
                                            }`}
                                        >
                                            {(
                                                results.discount.units -
                                                results.baseline.units
                                            ).toLocaleString()}
                                            (
                                            {(
                                                (results.discount.units /
                                                    results.baseline.units -
                                                    1) *
                                                100
                                            ).toFixed(1)}
                                            %)
                                        </td>
                                    </tr>
                                    <tr className="border-b">
                                        <td className="py-2 px-4 font-medium">
                                            Total Revenue
                                        </td>
                                        <td className="text-right py-2 px-4">
                                            $
                                            {results.baseline.revenue.toLocaleString()}
                                        </td>
                                        <td className="text-right py-2 px-4">
                                            $
                                            {results.discount.revenue.toLocaleString()}
                                        </td>
                                        <td
                                            className={`text-right py-2 px-4 ${
                                                results.discount.revenue >
                                                results.baseline.revenue
                                                    ? "text-green-600"
                                                    : "text-red-600"
                                            }`}
                                        >
                                            $
                                            {(
                                                results.discount.revenue -
                                                results.baseline.revenue
                                            ).toLocaleString()}
                                            (
                                            {(
                                                (results.discount.revenue /
                                                    results.baseline.revenue -
                                                    1) *
                                                100
                                            ).toFixed(1)}
                                            %)
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="py-2 px-4 font-medium">
                                            Gross Profit
                                        </td>
                                        <td className="text-right py-2 px-4">
                                            $
                                            {results.baseline.profit.toLocaleString()}
                                        </td>
                                        <td className="text-right py-2 px-4">
                                            $
                                            {results.discount.profit.toLocaleString()}
                                        </td>
                                        <td
                                            className={`text-right py-2 px-4 ${
                                                results.discount.profit >
                                                results.baseline.profit
                                                    ? "text-green-600"
                                                    : "text-red-600"
                                            }`}
                                        >
                                            $
                                            {(
                                                results.discount.profit -
                                                results.baseline.profit
                                            ).toLocaleString()}
                                            (
                                            {(
                                                (results.discount.profit /
                                                    results.baseline.profit -
                                                    1) *
                                                100
                                            ).toFixed(1)}
                                            %)
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}

export default Discounts;
